import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {LoadingButton} from '@mui/lab';
import {Box, Divider, IconButton, Tooltip} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {GeneralQueryParams} from '../../../interfaces/CommunicationNode';
import {ExportField} from '../../../interfaces/Export';
import {saveFile} from '../../../utils/file';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import ExportFieldForm from '../../common/ExportFieldForm';
import ExportPdfButton from '../../common/ExportPdfButton';
import ModalFixed from '../../common/ModalFixed';
import SnackbarMessages from '../../common/SnackbarMessages';

interface Props {
  params?: GeneralQueryParams;
  exportFields?: ExportField[];
}

const NetworkDiagnosticsGeneralExportButton = ({
  params,
  exportFields,
}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isOpened, setIsOpened] = useState(false);
  const [isExcelModalOpened, setIsExcelModalOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const handleExportExcel = async (fields: string[]) => {
    setIsExcelModalOpened(false);
    setIsFetching(true);
    try {
      const endpoint = `${apiBaseUrl}/communication-node/network-general-grid-export`;
      const resp = await API.get(endpoint, {
        params: {...params, fields},
        responseType: 'blob',
      });
      const filename =
        resp?.headers['content-disposition']?.split('filename=')?.[1] ||
        'network_general_report.xlsx';
      saveFile(resp.data, filename);
    } catch (error: unknown) {
      const messages = getMessagesFromApiError(error, 'STRA_net_diag_gen_exp');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setIsFetching(false);
  };

  return (
    <>
      <Tooltip title="Export">
        <FileDownloadIcon onClick={() => setIsOpened(true)} />
      </Tooltip>

      {isOpened ? (
        <ModalFixed open={isOpened} onClose={() => setIsOpened(false)}>
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            gap={3}
            p={3}
          >
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" alignItems="center" width="100%" gap={1.5}>
                <FileDownloadIcon sx={{color: 'primary.main'}} />
                <Box fontSize={24}>Export General Network Diagnostics</Box>
              </Box>
              <IconButton onClick={() => setIsOpened(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              position="relative"
              gap={3}
            >
              <Divider>Export</Divider>
              <LoadingButton
                fullWidth
                size="large"
                variant="contained"
                loading={isFetching}
                onClick={() => setIsExcelModalOpened(true)}
              >
                Export to Excel
              </LoadingButton>
              <ExportPdfButton
                endpoint="/communication-node/network-general-grid"
                defaultFilename="network_general_report.pdf"
                params={params as Record<string, unknown> | undefined}
                exportFields={exportFields}
              />
            </Box>
          </Box>
        </ModalFixed>
      ) : null}

      <ModalFixed
        open={isExcelModalOpened}
        onClose={() => setIsExcelModalOpened(false)}
      >
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          gap={3}
          p={3}
        >
          <ExportFieldForm
            exportFields={exportFields}
            loading={isFetching}
            onChange={handleExportExcel}
            close={() => setIsExcelModalOpened(false)}
          />
        </Box>
      </ModalFixed>
    </>
  );
};

export default NetworkDiagnosticsGeneralExportButton;
